import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";

const fmt = (n) => Number(n || 0).toLocaleString();

function formatMinutes(min) {
  const m = Math.max(0, Math.round(min || 0));
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

// Always-visible bottom bar: live deletion progress, indexing state, and lifetime time saved.
export default function StatusBar({ jobs, archive, refreshKey, onRetry }) {
  const [stats, setStats] = useState({ total_deleted: 0, time_saved_minutes: 0 });
  const active = jobs.filter((j) => ["queued", "running"].includes(j.status));
  const failedJob = active.length ? null : jobs.find((j) => j.failed > 0 && !j.retried_at && ["completed", "failed"].includes(j.status));

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/cleanup/stats");
      setStats(data);
    } catch (_) {
      /* keep last value */
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats, refreshKey, active.length]);

  useEffect(() => {
    if (!active.length) return undefined;
    const t = setInterval(loadStats, 5000);
    return () => clearInterval(t);
  }, [active.length, loadStats]);

  const processed = active.reduce((a, j) => a + (j.deleted || 0) + (j.failed || 0), 0);
  const total = active.reduce((a, j) => a + (j.total || 0), 0);
  const rate = active.reduce((a, j) => a + (j.rate_per_min || 0), 0);
  const pct = total ? Math.min(100, Math.round((processed / total) * 100)) : 0;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background px-4 py-2 flex items-center justify-between gap-4 text-xs font-mono"
      data-testid="status-bar"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {active.length ? (
          <>
            <span className="shrink-0" data-testid="status-bar-jobs">
              Deleting {fmt(processed)} / {fmt(total)}
              {rate ? ` · ${fmt(rate)}/min` : ""}
            </span>
            <Progress value={pct} className="h-1.5 max-w-xs" />
          </>
        ) : archive ? (
          <span data-testid="status-bar-archive">
            Archiving {fmt(archive.archived)} / {fmt(archive.total)}
          </span>
        ) : failedJob ? (
          <span data-testid="status-bar-failed">
            <span className="text-destructive">{fmt(failedJob.failed)} failed</span>{" "}
            <button type="button" className="underline" onClick={() => onRetry?.(failedJob.job_id)} data-testid="status-bar-retry">
              Retry
            </button>
          </span>
        ) : (
          <span className="text-muted-foreground" data-testid="status-bar-idle">No active deletions</span>
        )}
      </div>
      <div className="shrink-0" data-testid="status-bar-saved">
        <span className="text-muted-foreground">Time saved </span>
        <b>{formatMinutes(stats.time_saved_minutes)}</b>
        <span className="text-muted-foreground"> · {fmt(stats.total_deleted)} deleted</span>
      </div>
    </div>
  );
}
